import { writable, get } from 'svelte/store';

// Debug settings
export const DEBUG_CONFIG = writable({
  showDebugPanel: false,           // Show the debug panel UI component
  logContainerDetection: false,   // Log container detection messages
  logShapeMatching: false,        // Log shape matching details
  logFloorChanges: false,         // Log floor change events
  logSvgUpdates: false,           // Log SVG update events
  logServiceLocations: false,      // Log service location events
});

// Default configuration options for the map viewer
export const DEFAULT_MAP_CONFIG = {
  // Pan/zoom options
  minZoom: 0.8,
  maxZoom: 5,
  panLimit: 400,
  allowPan: true,        // Allow map panning
  allowZoom: true,       // Allow map zooming
  wheelZoom: true,       // Allow mouse wheel zooming
  panSensitivity: 25,     // Adjust pan speed
  zoomSensitivity: 25,    // Adjust zoom speed
  
  // UI controls
  showZoomControls: true,    // Show zoom in/out buttons
  showFloorControls: true,   // Show floor navigation controls
  showFloorNames: true,      // Show floor names instead of numbers
  showStatusDisplay: false,  // Show viewbox coordinates and zoom level
  showBoundaryIndicators: false, // Show visual indicators when limits are reached
  showStoreSidebar: true,    // Show the store search and list sidebar
  
  // Store interaction
  enableStoreClick: true,    // Enable clicking on stores
  enableStoreHover: true,    // Enable hover effects on stores
  showStorePins: true,       // Show store pins on the map
  showLabels: true,          // Show store labels on the map
  
  // Shape highlighting on hover/click
  shapeHighlighting: {
    fillColor: 'rgba(66, 133, 244, 0.95)',
    strokeColor: '#4285f4',
    strokeWidth: '1px',
    opacity: 0.4,
  },

  // Store pin styling
  storePins: {
    fillColor: {
      active: '#4285f4',
      inactive: '#FFFFFF',
    },
    strokeColor: {
      active: '#FFFFFF',
      inactive: '#4285f4',
    },
    strokeWidth: {
      active: '2px',
      inactive: '1px',
    },
    bigShapeSize: 1.6,
    mediumShapeSize: 1.3,
    smallShapeSize: 1.0,
    activeSizeMultiplier: 1.2,
    yOffset: 0,
  },
  
  // Label styling
  labelStyling: {
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    textColor: '#000000',
    textShadow: '0 0 2px rgba(255,255,255,0.7)', // A light shadow for better readability on dark shapes
    bigShapeFontSize: '16px',
    mediumShapeFontSize: '12px',
    smallShapeFontSize: '10px',
    yOffset: 12,
    showLabelOnHover: true, // Show label on hover regardless of zoom level
    hoverBackgroundColor: 'rgba(255, 255, 255, 0.8)',
    hoverBorderRadius: '4px',
    hoverVerticalPadding: '2px',
    hoverHorizontalPadding: '4px',
  },

  // Dynamic labeling based on zoom level and shape area
  dynamicLabeling: {
    enabled: true,
    smallAreaThreshold: 4000,
    mediumAreaThreshold: 10000,
    mediumLabelZoomThreshold: 150, // e.g., show medium labels at 150% zoom and above
    smallLabelZoomThreshold: 200,   // e.g., show small labels at 200% zoom and above
  },
  
  // Zoom level when focusing on a specific store (e.g., 2 = 200% zoom)
  storeZoomFactor: 2.5,

  // Service locations
  serviceLocations: {
    enabled: true,
    showServicePins: true,
    pinStyling: {
      scale: 0.75,
      fillColor: {
        inactive: '#666666',
        active: '#0066cc'
      },
      strokeColor: {
        inactive: '#333333',
        active: '#004499'
      },
      strokeWidth: {
        inactive: '0.75',
        active: '1'
      },
      yOffset: 0
    },
    labelStyling: {
      enabled: true,
      fontSize: '2px',
      fontWeight: 'bold',
      textColor: '#333333',
      textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
      yOffset: 10,
      showLabelOnHover: true,
      hoverBackgroundColor: 'rgba(255,255,255,0.9)',
      hoverBorderRadius: '4px',
      hoverVerticalPadding: '1px',
      hoverHorizontalPadding: '3px'
    }
  },
};

// Helper function to toggle debug panel UI (can be called from console)
export function toggleDebugPanel(): void {
  DEBUG_CONFIG.update(config => {
    const newState = !config.showDebugPanel;
    console.log(`Debug panel UI is now ${newState ? 'ENABLED' : 'DISABLED'}`);
    return { ...config, showDebugPanel: newState };
  });
}

// Helper functions to toggle individual debug categories
export function toggleContainerDetection(): void {
  DEBUG_CONFIG.update(config => {
    const newState = !config.logContainerDetection;
    console.log(`Container detection logging is now ${newState ? 'ENABLED' : 'DISABLED'}`);
    return { ...config, logContainerDetection: newState };
  });
}

export function toggleShapeMatching(): void {
  DEBUG_CONFIG.update(config => {
    const newState = !config.logShapeMatching;
    console.log(`Shape matching logging is now ${newState ? 'ENABLED' : 'DISABLED'}`);
    return { ...config, logShapeMatching: newState };
  });
}

export function toggleFloorChanges(): void {
  DEBUG_CONFIG.update(config => {
    const newState = !config.logFloorChanges;
    console.log(`Floor changes logging is now ${newState ? 'ENABLED' : 'DISABLED'}`);
    return { ...config, logFloorChanges: newState };
  });
}

export function toggleSvgUpdates(): void {
  DEBUG_CONFIG.update(config => {
    const newState = !config.logSvgUpdates;
    console.log(`SVG updates logging is now ${newState ? 'ENABLED' : 'DISABLED'}`);
    return { ...config, logSvgUpdates: newState };
  });
}

export function toggleServiceLocations(): void {
  DEBUG_CONFIG.update(config => {
    const newState = !config.logServiceLocations;
    console.log(`Service locations logging is now ${newState ? 'ENABLED' : 'DISABLED'}`);
    return { ...config, logServiceLocations: newState };
  });
}

// Test function to verify debug system is working
export function testDebugSystem(): void {
  console.log('=== DEBUG SYSTEM TEST ===');
  const config = get(DEBUG_CONFIG);
  console.log('Current debug config:', config);
  
  // Import and test debugLog function
  import('./store-layer/debugUtils.js').then(({ debugLog }) => {
    console.log('Testing each debug category:');
    debugLog('Container detection test message', 'containerDetection');
    debugLog('Shape matching test message', 'shapeMatching');
    debugLog('Floor changes test message', 'floorChanges');
    debugLog('SVG updates test message', 'svgUpdates');
    debugLog('Service locations test message', 'serviceLocations');
    console.log('=== END DEBUG TEST ===');
  });
}

// Make all toggle functions globally accessible for testing
if (typeof window !== 'undefined') {
  (window as any).toggleDebugPanel = toggleDebugPanel;
  (window as any).toggleContainerDetection = toggleContainerDetection;
  (window as any).toggleShapeMatching = toggleShapeMatching;
  (window as any).toggleFloorChanges = toggleFloorChanges;
  (window as any).toggleSvgUpdates = toggleSvgUpdates;
  (window as any).toggleServiceLocations = toggleServiceLocations;
  (window as any).testDebugSystem = testDebugSystem;
} 